import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import Icon from '../components/Icon';
import { useProducts } from '../product/ProductContext';
import {
    DEFAULT_CHANNELS, TYPE_STYLES, CREATE_KINDS, addDays, avatarColors, formatCount, formatLocalDate, formatRating,
    formatShortDate, getSetupSteps, getWeekStart, initials, kindForContentType, parseLocalDate, shortChannel, sourceName,
} from '../product/productUtils';
import { loadReportEntries } from '../utils/reportEntries';
import { findWinners, listMetrics } from '../utils/planInsights';
import './ideas.css';

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const percentChange = (current, previous) => {
    if (!previous) return null;
    return Math.round(((current - previous) / previous) * 100);
};

const Delta = ({ value, suffix = '%' }) => {
    if (value == null || Number.isNaN(value)) return <div className="ls-delta">—</div>;
    const direction = value > 0 ? 'is-up' : value < 0 ? 'is-down' : '';
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return <div className={`ls-delta ${direction}`}>{sign}{Math.abs(value)}{suffix}</div>;
};

// Views come from report entries, clicks from tracked links, both for the last 7 days vs the 7 before
const useLastSevenDays = (links) => useMemo(() => {
    const today = formatLocalDate(new Date());
    const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
    const previousDays = Array.from({ length: 7 }, (_, i) => addDays(today, i - 13));
    const entries = loadReportEntries();

    const viewsOn = (day) => entries
        .filter(entry => entry.postDate === day)
        .reduce((sum, entry) => sum + (Number(entry.views) || 0), 0);
    const clicksOn = (day) => links.reduce((sum, link) => sum + (link.byDay?.[day] || 0), 0);
    const sum = (list, fn) => list.reduce((total, day) => total + fn(day), 0);

    const views = sum(days, viewsOn);
    const previousViews = sum(previousDays, viewsOn);
    const clicks = sum(days, clicksOn);
    const previousClicks = sum(previousDays, clicksOn);
    const ctr = views ? (clicks / views) * 100 : null;
    const previousCtr = previousViews ? (previousClicks / previousViews) * 100 : null;

    // Bars show daily views, or clicks when no views are logged
    const series = views ? days.map(viewsOn) : days.map(clicksOn);
    const peak = Math.max(...series, 0);

    const recent = entries.filter(entry => days.includes(entry.postDate));
    const top = recent.sort((a, b) => (Number(b.views) || 0) - (Number(a.views) || 0))[0] || null;
    const topLink = top?.linkSlug ? links.find(link => link.slug === top.linkSlug) : null;

    return {
        views,
        clicks,
        ctr,
        viewsDelta: percentChange(views, previousViews),
        clicksDelta: percentChange(clicks, previousClicks),
        ctrDelta: ctr != null && previousCtr != null ? Math.round((ctr - previousCtr) * 10) / 10 : null,
        series,
        peak,
        seriesLabel: views ? 'views' : 'clicks',
        top: top && { ...top, link: topLink },
    };
}, [links]);

const WeekCard = ({ weekStart, plan, isLoading, onOpenPost }) => {
    const navigate = useNavigate();
    const today = formatLocalDate(new Date());
    const posts = plan?.posts || [];
    const scheduled = posts.filter(post => post.status !== 'posted' && post.status !== 'draft').length;
    const drafts = posts.filter(post => post.status === 'draft').length;
    const posted = posts.filter(post => post.status === 'posted').length;
    const summary = [
        `${scheduled} scheduled`,
        drafts ? `${drafts} draft${drafts === 1 ? '' : 's'}` : null,
        posted ? `${posted} posted` : null,
    ].filter(Boolean).join(' · ');

    return (
        <div className="ls-card ls-span-8" style={{ gap: 14 }}>
            <div className="ls-card-header">
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <span className="ls-card-title">This week</span>
                    {plan && <span className="ls-card-sub">{summary}</span>}
                </div>
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/plan')}>Open content plan</button>
            </div>
            {!isLoading && !plan ? (
                <div className="ls-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, padding: '24px 0', flex: 1, minHeight: 220 }}>
                    No plan for this week yet. The AI plans posts for your ICPs with views, likes and click targets.
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate('/dashboard/plan')}>
                        <Icon name="auto_awesome" size={18} />Plan this week
                    </button>
                </div>
            ) : (
                <div className="ls-week">
                    {WEEK_DAYS.map((dayName, index) => {
                        const date = addDays(weekStart, index);
                        const isToday = date === today;
                        const dayPosts = posts.filter(post => post.day === dayName);
                        const dayNumber = parseLocalDate(date).getDate();
                        return (
                            <div key={dayName} className={`ls-day${isToday ? ' is-today' : ''}`}>
                                <div className="ls-day-label">
                                    {isToday ? `Today ${dayNumber}` : <>{dayName.slice(0, 3)} <b>{dayNumber}</b></>}
                                </div>
                                {isLoading && index % 2 === 0 && <div className="ls-skeleton" style={{ height: 52 }} />}
                                {dayPosts.map(post => {
                                    const kind = kindForContentType(post.contentType);
                                    const type = CREATE_KINDS[kind].type;
                                    const colors = TYPE_STYLES[type];
                                    const isDraft = post.status === 'draft';
                                    const channel = shortChannel(post.platform);
                                    const label = [type, channel.toLowerCase() === type.toLowerCase() ? null : channel, post.time].filter(Boolean).join(' · ');
                                    return (
                                        <button
                                            type="button"
                                            key={post.id}
                                            className={`ls-post-chip${isDraft ? ' is-draft' : ''}`}
                                            style={{ background: colors.bg }}
                                            onClick={() => onOpenPost(post, kind)}
                                            title={post.hook || post.idea}
                                        >
                                            <div className="ls-post-chip-label" style={{ color: isDraft ? 'var(--warn-text)' : colors.fg }}>
                                                {post.status === 'posted' && <Icon name="check_circle" size={13} />}
                                                {label}{isDraft ? ' · Draft' : ''}
                                            </div>
                                            {post.theme || post.hook}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// This week's posted posts that reached a target: views and likes logged in
// Reports (or at the last review), clicks from tracked links
const useWins = (plan) => useMemo(() => {
    const posts = Array.isArray(plan?.posts) ? plan.posts.filter(post => post && typeof post === 'object') : [];
    const entries = loadReportEntries();
    const getActual = (post) => {
        const entry = entries.find(item => item?.planPostId === post.id);
        return {
            views: entry ? Number(entry.views) || 0 : null,
            likes: entry ? Number(entry.likes) || 0 : null,
            clicks: post.linkSlug ? Number(post.clicks) || 0 : null,
        };
    };
    const winners = findWinners(posts, getActual, plan?.winners);
    return posts.filter(post => winners[post.id]).map(post => ({ ...post, beat: winners[post.id] }));
}, [plan]);

const WinsLine = ({ wins, isLoading }) => {
    const navigate = useNavigate();
    const [first] = wins;
    const title = first && (typeof first.theme === 'string' && first.theme ? first.theme : first.hook);
    return (
        <div className="ls-wins-line">
            <Icon name="emoji_events" size={16} />
            <span className="ls-wins-line-text">
                {isLoading ? <span className="ls-skeleton-text" style={{ width: '12em' }} />
                    : wins.length ? (
                        <><b>Wins this week: {wins.length}</b>{title ? ` · “${title}” beat its ${listMetrics(first.beat)} target` : ''}</>
                    ) : 'Wins this week: none yet. Posts that beat a target show up here.'}
            </span>
            {!isLoading && wins.length > 0 && (
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/plan')}>See</button>
            )}
        </div>
    );
};

const ResultsCard = ({ stats, icps, isLoading, wins = [] }) => {
    const navigate = useNavigate();
    const topIcp = stats.top?.link?.icpId && icps.find(icp => icp.id === stats.top.link.icpId);
    // Clicks come with the tracked links; until then a placeholder holds the number's place
    const pending = isLoading && <span className="ls-skeleton-text" />;
    return (
        <div className="ls-card ls-span-4" style={{ gap: 16 }}>
            <div className="ls-card-header">
                <span className="ls-card-title">Last 7 days</span>
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/reports')}>Reports</button>
            </div>
            <div className="ls-metrics">
                <div>
                    <div className="ls-metric-label">Views</div>
                    <div className="ls-metric-value">{formatCount(stats.views)}</div>
                    <Delta value={stats.viewsDelta} />
                </div>
                <div>
                    <div className="ls-metric-label">Link clicks</div>
                    <div className="ls-metric-value">{pending || formatCount(stats.clicks)}</div>
                    <Delta value={isLoading ? null : stats.clicksDelta} />
                </div>
                <div>
                    <div className="ls-metric-label">CTR</div>
                    <div className="ls-metric-value">{pending || (stats.ctr == null ? '—' : `${stats.ctr.toFixed(1)}%`)}</div>
                    <Delta value={isLoading ? null : stats.ctrDelta} suffix="" />
                </div>
            </div>
            <div className="ls-bars" role="img" aria-label={`Daily ${stats.seriesLabel}, last 7 days: ${stats.series.join(', ')}`}>
                {stats.series.map((value, index) => (
                    <div
                        key={index}
                        className={stats.peak > 0 && value === stats.peak ? 'is-peak' : ''}
                        style={{ height: `${stats.peak ? Math.max(4, (value / stats.peak) * 100) : 4}%` }}
                        title={`${value.toLocaleString()} ${stats.seriesLabel}`}
                    />
                ))}
            </div>
            <div className="ls-divider-top">
                <div className="ls-metric-label">Top post</div>
                {stats.top ? (
                    <>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{stats.top.description || 'Untitled post'}</div>
                        <div style={{ fontSize: 13, color: 'var(--secondary)' }}>
                            {[
                                stats.top.link?.platform || stats.top.platform,
                                `${formatCount(stats.top.views)} views`,
                                stats.top.link ? `${formatCount(stats.top.link.clicks)} clicks` : null,
                                topIcp ? `${topIcp.name} ICP` : null,
                            ].filter(Boolean).join(' · ')}
                        </div>
                    </>
                ) : (
                    <div className="ls-empty" style={{ fontSize: 13 }}>
                        Log views in Reports and share tracked links to see what's working.
                    </div>
                )}
                <WinsLine wins={wins} isLoading={isLoading} />
            </div>
        </div>
    );
};

const ProductCard = ({ product }) => {
    const navigate = useNavigate();
    const chips = [
        product.platforms?.join(' & '),
        product.pricing && product.pricing.length <= 32 ? product.pricing : null,
        formatRating(product.proof),
    ].filter(Boolean);
    const sourceNames = (product.sources || []).filter(s => s.type === 'landing' || s.type === 'store').map(sourceName).slice(0, 2);
    return (
        <div className="ls-card ls-span-3">
            <div className="ls-card-header">
                <span className="ls-card-title">Product</span>
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/profile')}>Edit</button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--body)' }}>
                {product.oneLiner || <span className="ls-muted">Add a one-liner so every post starts from the same pitch.</span>}
            </div>
            {chips.length > 0 && <div className="ls-chips">{chips.map(chip => <span key={chip} className="ls-chip">{chip}</span>)}</div>}
            <div className="ls-card-footer">
                {sourceNames.length ? `Synced from ${sourceNames.join(' and ')}` : product.sources?.length ? 'From your uploads' : 'Filled in manually'}
                {product.syncedAt ? ` · ${formatShortDate(product.syncedAt)}` : ''}
            </div>
        </div>
    );
};

const IcpCard = ({ icps, links, isLoading }) => {
    const navigate = useNavigate();
    const clicksByIcp = links.reduce((acc, link) => {
        if (link.icpId) acc[link.icpId] = (acc[link.icpId] || 0) + (link.clicksLast7Days || 0);
        return acc;
    }, {});
    const total = Object.values(clicksByIcp).reduce((sum, value) => sum + value, 0);
    return (
        <div className="ls-card ls-span-3">
            <div className="ls-card-header">
                <span className="ls-card-title">ICPs</span>
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/icps')}>View all</button>
            </div>
            {icps.length === 0 && <div className="ls-empty">No customer profiles yet. Add one so posts speak to someone specific.</div>}
            {icps.slice(0, 3).map((icp, index) => {
                const colors = avatarColors(index);
                return (
                    <button
                        type="button"
                        key={icp.id}
                        className="ls-row"
                        style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left', color: 'inherit' }}
                        onClick={() => navigate(`/dashboard/icps/${icp.id}`)}
                    >
                        <div className="ls-avatar" style={{ width: 32, height: 32, fontSize: 12, background: colors.bg, color: colors.fg }}>
                            {initials(icp.name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="ls-row-title">{icp.name}</div>
                            <div className="ls-row-sub">{icp.descriptor}</div>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-text)', minWidth: 30, textAlign: 'right' }}>
                            {total ? `${Math.round(((clicksByIcp[icp.id] || 0) / total) * 100)}%` : ''}
                        </span>
                    </button>
                );
            })}
            <div className="ls-card-footer">
                {isLoading ? <span className="ls-skeleton-text" style={{ width: 160 }} /> : total ? "Share of this week's clicks" : 'Click share shows once tracked links get clicks'}
            </div>
        </div>
    );
};

const VoiceCard = ({ voice }) => {
    const navigate = useNavigate();
    const hasVoice = voice && (voice.summary || voice.avoid?.length);
    return (
        <div className="ls-card ls-span-3">
            <div className="ls-card-header">
                <span className="ls-card-title">Brand voice</span>
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/voice')}>Tune</button>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
                {voice?.summary || <span className="ls-muted" style={{ fontWeight: 400 }}>Not set yet</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="ls-slider-labels"><span>Casual</span><span>Formal</span></div>
                <div className="ls-slider-track"><div style={{ left: `${voice?.formality ?? 30}%` }} /></div>
                <div className="ls-slider-labels" style={{ marginTop: 4 }}><span>Playful</span><span>Serious</span></div>
                <div className="ls-slider-track"><div style={{ left: `${voice?.seriousness ?? 35}%` }} /></div>
            </div>
            <div className="ls-card-footer">
                {hasVoice && voice.avoid?.length ? `Avoid: ${voice.avoid.map(word => `“${word}”`).join(', ')}` : 'Words to avoid go here'}
            </div>
        </div>
    );
};

const ChannelsCard = ({ product, onSave }) => {
    const navigate = useNavigate();
    const channels = product.channels?.length
        ? product.channels
        : DEFAULT_CHANNELS.map(name => ({ name, connected: false, handle: '' }));
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const connected = channels.filter(channel => channel.connected).length;

    const save = (next) => onSave(next);

    const addChannel = (event) => {
        event.preventDefault();
        const name = newName.trim();
        if (name && !channels.some(channel => channel.name.toLowerCase() === name.toLowerCase())) {
            save([...channels, { name, connected: false, handle: '' }]);
        }
        setAdding(false);
        setNewName('');
    };

    return (
        <div className="ls-card ls-span-3" id="channels" style={{ gap: 10 }}>
            <div className="ls-card-header">
                <span className="ls-card-title">Channels</span>
                <span className="ls-card-sub">{connected} of {channels.length}</span>
            </div>
            {channels.map(channel => (
                <div key={channel.name} className="ls-channel-row">
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={channel.handle}>
                        {channel.name}
                    </span>
                    {channel.connected ? (
                        <button
                            type="button"
                            className="ls-connected"
                            style={{ border: 0, background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                            title={`${channel.handle} · click to disconnect`}
                            onClick={() => window.confirm(`Disconnect ${channel.name}?`) && save(channels.map(c => (
                                c.name === channel.name ? { ...c, connected: false } : c
                            )))}
                        >
                            Connected
                        </button>
                    ) : (
                        <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <button
                                type="button"
                                className="ls-btn ls-btn-pill"
                                onClick={() => navigate('/dashboard/channels')}
                            >
                                Connect
                            </button>
                            <button
                                type="button"
                                className="ls-chip-remove"
                                aria-label={`Remove ${channel.name}`}
                                title="I don't post here"
                                onClick={() => save(channels.filter(c => c.name !== channel.name))}
                            >
                                <Icon name="close" size={16} />
                            </button>
                        </span>
                    )}
                </div>
            ))}
            {adding ? (
                <form onSubmit={addChannel} style={{ display: 'flex', gap: 6 }}>
                    <input
                        className="ls-input is-compact"
                        autoFocus
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="e.g. LinkedIn"
                        aria-label="Channel name"
                        onKeyDown={(e) => e.key === 'Escape' && setAdding(false)}
                    />
                    <button type="submit" className="ls-btn ls-btn-pill">Add</button>
                </form>
            ) : (
                <button type="button" className="ls-link" style={{ marginTop: 'auto' }} onClick={() => setAdding(true)}>+ Add channel</button>
            )}
        </div>
    );
};

const SetupBanner = ({ product, icpCount }) => {
    const navigate = useNavigate();
    const steps = getSetupSteps(product, icpCount);
    const done = steps.filter(step => step.done).length;
    if (done === steps.length) return null;
    const next = steps.find(step => !step.done);

    const open = (step) => {
        if (step.id === 'channels') {
            document.getElementById('channels')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        navigate(step.to);
    };

    return (
        <div className="ls-card ls-setup">
            <div className="ls-setup-summary">
                <div style={{ fontSize: 15, fontWeight: 600 }}>Finish setting up {product.name}</div>
                <div className="ls-progress"><div style={{ width: `${(done / steps.length) * 100}%` }} /></div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>{done} of {steps.length} done</div>
            </div>
            <div className="ls-setup-steps">
                {steps.map(step => {
                    const state = step.done ? 'is-done' : step === next ? 'is-current' : '';
                    return (
                        <button type="button" key={step.id} className={`ls-step-chip ${state}`} onClick={() => open(step)}>
                            <Icon
                                name={step.done ? 'check_circle' : 'radio_button_unchecked'}
                                size={17}
                                color={step.done ? 'var(--accent-text)' : step === next ? 'var(--accent-strong)' : 'var(--disabled)'}
                            />
                            {step.label}
                        </button>
                    );
                })}
            </div>
            <button type="button" className="ls-link" style={{ fontSize: 14 }} onClick={() => open(next)}>
                Continue<Icon name="arrow_forward" size={18} />
            </button>
        </div>
    );
};

const DashboardScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentProduct: product, icps, updateProduct } = useProducts();
    const [plan, setPlan] = useState(null);
    const [links, setLinks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const weekStart = getWeekStart();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [planResult, linksResult] = await Promise.allSettled([api.getPlan(weekStart), api.listTrackedLinks()]);
            if (cancelled) return;
            setPlan(planResult.status === 'fulfilled' ? planResult.value : null);
            setLinks(linksResult.status === 'fulfilled' ? linksResult.value : []);
            if (planResult.status === 'rejected' && linksResult.status === 'rejected') {
                setError("Couldn't load this week's plan and results. Check that the server is running.");
            }
            setIsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [weekStart]);

    // /dashboard#channels scrolls to the channels card
    useEffect(() => {
        if (location.hash === '#channels' && !isLoading) {
            document.getElementById('channels')?.scrollIntoView({ block: 'center' });
        }
    }, [location.hash, isLoading]);

    const stats = useLastSevenDays(links);
    const wins = useWins(plan);

    const openPost = (post, kind) => {
        navigate(`/dashboard/${kind}${post.icpId ? `?icp=${post.icpId}` : ''}`, {
            state: { seed: { topic: post.idea || post.theme, hook: post.hook, platform: post.platform } },
        });
    };

    const saveChannels = async (channels) => {
        try {
            await updateProduct(product.id, { channels });
        } catch (e) {
            setError("Couldn't save channels. Please try again.");
        }
    };

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Dashboard · Week of {formatShortDate(parseLocalDate(weekStart))}</div>
                    <h1 className="ls-page-title">{product.name}</h1>
                </div>
                <div className="ls-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate('/dashboard/plan?week=next')}>
                        <Icon name="auto_awesome" size={18} />Plan next week
                    </button>
                    <button type="button" className="ls-btn ls-btn-primary" onClick={() => navigate('/dashboard/social')}>
                        <Icon name="add" size={18} />Create content
                    </button>
                </div>
            </div>

            {error && (
                <div className="ls-error"><Icon name="error" />{error}</div>
            )}

            <SetupBanner product={product} icpCount={icps.length} />

            <div className="ls-grid-12">
                <WeekCard weekStart={weekStart} plan={plan} isLoading={isLoading} onOpenPost={openPost} />
                <ResultsCard stats={stats} icps={icps} isLoading={isLoading} wins={wins} />
                <ProductCard product={product} />
                <IcpCard icps={icps} links={links} isLoading={isLoading} />
                <VoiceCard voice={product.voice} />
                <ChannelsCard product={product} onSave={saveChannels} />
            </div>
        </>
    );
};

export default DashboardScreen;
